import {controller, target, attr} from '@github/catalyst'
import type {IncludeFragmentElement} from '@github/include-fragment-element'
import type {FeedFilterElement} from './feed-filter-element'
import './feed-filter-element'
import {updateUrl} from '@github-ui/history'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

@controller('feed-container')
export class FeedContainerElement extends HTMLElement {
  FILTER_URL = '/conduit/filter'
  TOPIC_FEEDS_URL = '/conduit/topic_feeds'
  FOR_YOU_FEED_URL = '/conduit/for_you_feed'
  FEED_POST_TAGS_URL = '/feed_posts/tags_menu'

  private FOR_YOU_TOPIC = 'for-you'

  contentCache: Map<string, string> = new Map()

  declare private contentAbortController: AbortController | null

  @attr org = ''
  @attr activeTopic = this.org || this.FOR_YOU_TOPIC

  @target declare feedTitle: HTMLElement
  @target declare spinner: HTMLElement
  @target declare filter: FeedFilterElement
  @target declare filterTrigger: HTMLDetailsElement
  @target declare filterCounter: HTMLElement
  @target declare filterFragment: IncludeFragmentElement
  @target declare content: HTMLElement

  get topicFeedFilterUrl() {
    let url = this.FILTER_URL
    if (this.org) {
      url += `?org=${this.org}`
    } else if (this.activeTopic !== this.FOR_YOU_TOPIC) {
      url += `?is_topic=true`
    }
    return url
  }

  connectedCallback() {
    this.addEventListener('feed-filter:apply', this.handleFilterApply, true)
    this.addEventListener('feed-filter-next:active-count', this.handleFilterCount, true)

    const heightObserver = new MutationObserver(() => {
      const frame = this.querySelector('#conduit-feed-frame')
      if (frame) {
        const height = sessionStorage.getItem('github:dashboard:scrollHeight')
        if (height) {
          window.scrollTo(0, +height)
          sessionStorage.removeItem('github:dashboard:scrollHeight')
        }
        heightObserver.disconnect()
      }
    })

    if (this.content) {
      heightObserver.observe(this.content, {childList: true, subtree: true})
      // delay loading filter until listeners/observers are initialized
      // to prevent race condition
      this.filterFragment.src = this.topicFeedFilterUrl
    }
  }

  async handleForYouFeedItemClick() {
    const previousTopic = this.activeTopic

    this.updateActiveTopic('for-you')
    this.updateTitle('Home')
    this.updateUrlParams()

    await this.filter.setFeedFilterSettings(previousTopic)
    await this.updateContent(this.FOR_YOU_FEED_URL, previousTopic)
  }

  async handleTopicFeedItemClick(name: string, displayName: string) {
    if (!name) throw new Error('topic name is empty.')
    const previousTopic = this.activeTopic

    this.updateUrlParams(name)
    this.updateActiveTopic(name)
    this.updateTitle(displayName)
    await this.updateContent(this.getTopicFeedUrl(name), previousTopic)
    await this.filter.setFeedFilterSettings(previousTopic)
  }

  private getTopicFeedUrl(topic: string) {
    return `${this.TOPIC_FEEDS_URL}?topic=${topic}`
  }

  private updateActiveTopic(name: string) {
    this.activeTopic = name
  }

  private async updateContent(url: string, previousTopic: string | null = null) {
    try {
      this.spinner.toggleAttribute('hidden', false)
      this.content.toggleAttribute('hidden', true)

      if (this.contentAbortController) {
        this.contentAbortController.abort()
      } else if (previousTopic) {
        this.cacheContent(previousTopic)
      }

      // use cached feed content for a particular topic if already loaded.
      const cachedContent = this.contentCache.get(this.getCacheKey(this.activeTopic))
      if (cachedContent) {
        // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- pre-existing innerHTML sink grandfathered when enabling the rule; not migrated in this rollout
        this.content.innerHTML = cachedContent
      } else {
        this.contentAbortController = new AbortController()

        const response = await fetch(url, {
          method: 'GET',
          signal: this.contentAbortController.signal,
          credentials: 'same-origin',
          headers: {
            ...getBaseFetchHeaders(),
          },
        })

        const html = await response.text()
        // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- pre-existing innerHTML sink grandfathered when enabling the rule; not migrated in this rollout
        this.content.innerHTML = html
        this.contentAbortController = null
      }
    } catch (err) {
      // skip abort error
      if (err instanceof DOMException) return
      reportError(err)
    }

    this.spinner.toggleAttribute('hidden', true)
    this.content.toggleAttribute('hidden', false)
  }

  private handleFilterApply(e: Event) {
    const detail = (e as CustomEvent).detail
    // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- pre-existing innerHTML sink grandfathered when enabling the rule; not migrated in this rollout
    this.content.innerHTML = detail.updatedFeedContent
  }

  private handleFilterCount(e: Event) {
    const {count, maximumFilterCapacity} = (e as CustomEvent).detail

    this.filterCounter.hidden = count === 0 || count === maximumFilterCapacity
    this.filterCounter.textContent = count
  }

  private updateUrlParams(topic?: string) {
    const url = new URL('', window.origin)
    if (topic) {
      url.searchParams.set('topic', topic)
    }

    updateUrl(url.href)
  }

  private updateTitle(topic: string) {
    this.feedTitle.textContent = topic
  }

  private cacheContent(topic: string) {
    this.contentCache.set(this.getCacheKey(topic), this.content.innerHTML)
  }

  private getCacheKey(topic: string) {
    return `${topic}:${this.filter.value}`
  }

  // This function updates the tags menu in the feed post dialog
  // and conditionally removes any automatically-attributed tag based on
  // changes made while the user was in the topics context in the dashboard.
  private async updateFeedPostTags() {
    const activeTopicIsForYou = this.activeTopic === 'for-you'
    const topicTags = document.querySelector('.js-feed-post-tags')
    const activeTag = document.querySelector('.js-feed-post-active-tag-wrapper')
    const selectedTagIdEl = document.querySelector('.js-feed-post-form-selected-topic-id') as HTMLInputElement
    const selectedTagNameEl = document.querySelector('.js-feed-post-form-selected-topic-name') as HTMLInputElement

    if (!topicTags || !activeTag || !selectedTagIdEl || !selectedTagNameEl) return

    // if the user is returned to the for_you feed with no selected topic,
    // clear the hidden selected tag in the form and hide the active tag.
    if (activeTopicIsForYou) {
      selectedTagIdEl.value = ''
      selectedTagNameEl.value = ''
      activeTag.setAttribute('hidden', 'true')
    }

    // fetch and replace the tag menu since there may have been topics starred or unstarred...
    const selectedTagName = selectedTagNameEl.value
    const url = new URL(this.FEED_POST_TAGS_URL, window.origin)
    if (selectedTagName) url.searchParams.set('context', selectedTagName)

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          ...getBaseFetchHeaders(),
        },
      })

      const html = await response.text()
      // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- pre-existing innerHTML sink grandfathered when enabling the rule; not migrated in this rollout
      topicTags.innerHTML = html
    } catch (err) {
      if (err instanceof DOMException) return
      reportError(err)
    }
  }
}
